import React from 'react';

interface CardProps {
    children: React.ReactNode;
    className?: string;
    variant?: 'default' | 'elevated' | 'outline';
    padding?: 'none' | 'sm' | 'md' | 'lg';
    rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
    onClick?: () => void;
    backgroundClassName?: string;
}

interface CardHeaderProps {
    children: React.ReactNode;
    className?: string;
}

interface CardTitleProps {
    children: React.ReactNode;
    className?: string;
}

interface CardContentProps {
    children: React.ReactNode;
    className?: string;
}

type CardComponent = React.FC<CardProps> & {
    Header: React.FC<CardHeaderProps>;
    Title: React.FC<CardTitleProps>;
    Content: React.FC<CardContentProps>;
};

const Card = (({
    children,
    className = '',
    variant = 'default',
    padding = 'none',
    rounded = 'lg',
    onClick,
    backgroundClassName = 'bg-white',
}: CardProps) => {
    const baseClasses = `${backgroundClassName} transition-all duration-200`;

    const variantClasses = {
        default: 'border border-mld-panel-border',
        elevated: 'border border-mld-panel-border',
        outline: 'border-2 border-mld-panel-border hover:border-mld-primary-200',
    };

    const paddingClasses = {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
    };

    const roundedClasses = {
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        '3xl': 'rounded-3xl',
    };

    const cursorClass = onClick ? 'cursor-pointer' : '';

    const allClasses = [
        baseClasses,
        variantClasses[variant],
        paddingClasses[padding],
        roundedClasses[rounded],
        cursorClass,
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={allClasses} onClick={onClick}>
            {children}
        </div>
    );
}) as CardComponent;

const CardHeader: React.FC<CardHeaderProps> = ({children, className = ''}) => {
    return <div className={`border-b border-gray-200 px-6 py-4 ${className}`}>{children}</div>;
};

const CardTitle: React.FC<CardTitleProps> = ({children, className = ''}) => {
    return <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>{children}</h3>;
};

const CardContent: React.FC<CardContentProps> = ({children, className = ''}) => {
    return <div className={`px-6 py-4 ${className}`}>{children}</div>;
};

// Attach sub-components to Card
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Content = CardContent;

export default Card;
